"use client";
import type { Placement } from "@floating-ui/react";
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { ReactNode, useRef, useState } from "react";
import { cn } from "@/lib/utils/cn";

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  placement?: Placement;
  className?: string;
}

export function Tooltip({
  children,
  content,
  placement = "top",
  className,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
  });

  const hover = useHover(context, {
    handleClose: safePolygon({ blockPointerEvents: true, requireIntent: true }),
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  return (
    <>
      <span
        ref={refs.setReference}
        className={className ?? "inline-flex"}
        {...getReferenceProps()}
      >
        {children}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={cn("z-50 max-w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg transition-opacity duration-150", isPositioned ? "opacity-100" : "opacity-0")}
            {...getFloatingProps()}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill="white"
              strokeWidth={1}
              stroke="#e5e7eb"
              width={12}
              height={6}
            />
            <div className="text-xs leading-relaxed text-gray-600">{content}</div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
